import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { StaffStateService } from '../../../services/staff-state.service';
import { StaffComplete } from '../../../models/StaffComplete';
import { StaffEditPayload } from '../../../models/StaffEdit';
import { StaffService } from '../../../services/staff.service';
import { BENEFIT_TYPE_OPTIONS } from '../../../models/BenefitType';
import { Department } from '../../../models/Department';
import { Position } from '../../../models/Position';
import { DepartmentService } from '../../../services/department.service';
import { PositionService } from '../../../services/position.service';
import { CostCentersService } from '../../../services/cost-centers.service';
import { CostCenter } from '../../../models/CostCenter';
import { environment } from '../../../../environments/environment';
import { PermissionService } from '../../../services/permission.service';
import { ERPPermissions } from '../../../constants/permissions.constants';

@Component({
  selector: 'app-staff-details',
  templateUrl: './staff-details.component.html',
  styleUrls: ['./staff-details.component.css']
})
export class StaffDetailsComponent implements OnInit, OnDestroy {
  isEditing = false;
  staffName = '';
  staffComplete: StaffComplete | null = null;
  error: string | null = null;
  successMessage: string | null = null;
  loading = false;
  saving = false;
  sub?: Subscription;

  // Enums para turno e horário
  readonly SHIFT_OPTIONS = [
    { value: 'Diurno', label: 'Diurno ' },
    { value: 'Noturno', label: 'Noturno ' },
    { value: 'Integral', label: 'Integral ' }
  ];

  readonly WORK_START_OPTIONS = [
    { value: '05:00', label: '05:00' },
    { value: '06:00', label: '06:00' },
    { value: '07:00', label: '07:00' },
    { value: '08:00', label: '08:00' },
    { value: '09:00', label: '09:00' },
    { value: '10:00', label: '10:00' },
    { value: '11:00', label: '11:00' },
    { value: '12:00', label: '12:00' },
    { value: '13:00', label: '13:00' },
    { value: '14:00', label: '14:00' },
    { value: '15:00', label: '15:00' },
    { value: '16:00', label: '16:00' },
    { value: '17:00', label: '17:00' },
    { value: '18:00', label: '18:00' },
    { value: '19:00', label: '19:00' },
    { value: '20:00', label: '20:00' },
    { value: '21:00', label: '21:00' },
    { value: '22:00', label: '22:00' },
    { value: '23:00', label: '23:00' },
    { value: '00:00', label: '00:00' }
  ];

  // Unidades e centros de custo
  readonly UNIT_OPTIONS = [
    { value: 'Matriz Santa Marina', label: 'Matriz Santa Marina' },
    { value: 'Matriz Butantã', label: 'Matriz Butantã' }
  ];

  // Opções para bancos
  readonly BANKS_OPTIONS = [
    { value: 'Banco do Brasil', label: 'Banco do Brasil' },
    { value: 'Caixa Econômica Federal', label: 'Caixa Econômica Federal' },
    { value: 'Itaú', label: 'Itaú' },
    { value: 'Bradesco', label: 'Bradesco' },
    { value: 'Santander', label: 'Santander' },
    { value: 'Banco Inter', label: 'Banco Inter' },
    { value: 'Nubank', label: 'Nubank' },
    { value: 'Outro', label: 'Outro' }
  ];

  // Opções para tipos de conta
  readonly ACCOUNT_TYPE_OPTIONS = [
    { value: 'Corrente', label: 'Conta Corrente' },
    { value: 'Poupança', label: 'Conta Poupança' },
    { value: 'Salário', label: 'Conta Salário' },
    { value: 'Pagamento', label: 'Conta Pagamento' }
  ];

  // Opções de tipos de benefícios
  readonly BENEFIT_TYPE_OPTIONS = BENEFIT_TYPE_OPTIONS;

  // Tipos de documentos disponíveis
  readonly DOCUMENT_TYPES = [
    { value: 'photo', label: 'Foto do Perfil', icon: 'fas fa-camera', endpoint: 'upload-photo' },
    { value: 'rg', label: 'RG', icon: 'fas fa-id-card', endpoint: 'upload-document' },
    { value: 'cpf', label: 'CPF', icon: 'fas fa-id-card', endpoint: 'upload-document' },
    { value: 'cnh', label: 'CNH', icon: 'fas fa-id-card', endpoint: 'upload-document' },
    { value: 'workCard', label: 'Carteira de Trabalho', icon: 'fas fa-id-card', endpoint: 'upload-document' },
    { value: 'diploma', label: 'Diploma', icon: 'fas fa-graduation-cap', endpoint: 'upload-document' },
    { value: 'curriculum', label: 'Currículo', icon: 'fas fa-file-alt', endpoint: 'upload-document' },
    { value: 'residenceProof', label: 'Comprovante de Residência', icon: 'fas fa-home', endpoint: 'upload-document' },
    { value: 'medicalCertificate', label: 'Atestado Médico', icon: 'fas fa-heartbeat', endpoint: 'upload-document' }
  ];

  // Tipo especial para "Outros" documentos (múltiplos)
  readonly OTHER_DOCUMENT_TYPE = { value: 'other', label: 'Outros Documentos', icon: 'fas fa-folder-open', endpoint: 'upload-document' };
  
  // Array para armazenar múltiplos documentos "outros"
  otherDocuments: any[] = [];

  // Abas disponíveis
  availableTabs = [
    { id: 'basic', label: 'Dados Pessoais', icon: 'fas fa-user', required: true, completed: false },
    { id: 'contract', label: 'Contrato', icon: 'fas fa-file-contract', required: true, completed: false },
    { id: 'banking', label: 'Bancário', icon: 'fas fa-university', required: false, completed: false },
    { id: 'contacts', label: 'Contatos', icon: 'fas fa-phone', required: false, completed: false },
    { id: 'addresses', label: 'Endereços', icon: 'fas fa-map-marker-alt', required: false, completed: false },
    { id: 'salary', label: 'Salário', icon: 'fas fa-money-bill-wave', required: false, completed: false },
    { id: 'benefits', label: 'Benefícios', icon: 'fas fa-gift', required: false, completed: false },
    { id: 'documents', label: 'Documentos', icon: 'fas fa-paperclip', required: false, completed: false }
  ];

  // Aba ativa
  activeTab = 'basic';
  readonly Permissions = ERPPermissions;

  // Dados de edição
  editData: StaffEditPayload | null = null;

  // Sistema de upload de documentos
  uploadProgress: { [key: string]: number } = {};
  uploadStatus: { [key: string]: 'pending' | 'uploading' | 'success' | 'error' } = {};
  uploadedDocuments: { [key: string]: any } = {};

  // Departamentos e cargos (para replicar lógica de cadastro no modo edição)
  departments: Department[] = [];
  positions: Position[] = [];
  selectedDepartmentId: number | null = null;
  isLoadingDepartments = false;
  isLoadingPositions = false;

  // Centros de custo
  costCenters: CostCenter[] = [];
  isLoadingCostCenters = false;

  constructor(
    private route: ActivatedRoute, 
    private router: Router,
    private staffState: StaffStateService,
    private staffService: StaffService,
    private cdr: ChangeDetectorRef,
    private departmentService: DepartmentService,
    private positionService: PositionService,
    private costCentersService: CostCentersService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.applySalaryPermissionTabs();
    const id = this.route.snapshot.paramMap.get('id');
    
    // Recuperar dados enviados via state
    const nav = history.state as { 
      name?: string; 
      staffComplete?: StaffComplete;
      error?: string;
    };
    
    this.staffName = nav?.name || '';
    this.staffComplete = nav?.staffComplete || null;
    this.error = nav?.error || null;

    if (!this.staffComplete && id) {
      this.loading = true;
      this.staffService.getStaffComplete(Number(id)).subscribe({
        next: (data) => {
          this.staffComplete = data;
          this.loading = false;
          this.prepareViewData();
          this.updateAvailableTabs();
          this.loadCostCenters();
        },
        error: (err) => {
          console.error('Erro ao recuperar dados do servidor:', err);
          this.error = 'Erro ao carregar dados do funcionário';
          this.loading = false;
        }
      });
    } else {
      // Iniciar em modo de visualização (não edição)
      this.isEditing = false;
      this.prepareViewData();
      this.updateAvailableTabs();
      this.loadCostCenters();
    }
  }

  private applySalaryPermissionTabs(): void {
    const canViewSalary = this.permissionService.hasPermission(this.Permissions.VISUALIZAR_SALARIO);
    if (canViewSalary) {
      return;
    }

    this.availableTabs = this.availableTabs.filter(tab => tab.id !== 'salary' && tab.id !== 'benefits');
    if (this.activeTab === 'salary' || this.activeTab === 'benefits') {
      this.activeTab = this.availableTabs[0]?.id || 'basic';
    }
  }

  // Preparar dados para visualização (mapear staffComplete para editData)
  prepareViewData(): void {
    if (!this.staffComplete) return;

    this.mapEnumValuesFromDatabase();
    
    // Preparar dados para visualização (mesma estrutura do editData)
    this.editData = {
      staff: {
        fullName: this.staffComplete.Staff.FullName || '',
        registration: this.staffComplete.Staff.Registration || '',
        position: this.staffComplete.Staff.Position || '',
        positionId: this.staffComplete.Staff.PositionId || null,
        department: this.staffComplete.Staff.Department || '',
        departmentId: this.staffComplete.Staff.DepartmentId || null,
        manager: this.staffComplete.Staff.Manager || '',
        managerId: (this.staffComplete.Staff as any).ManagerId || null,
        hireDate: this.staffComplete.Staff.HireDate || '',
        contractType: this.staffComplete.Staff.ContractType || 'CLT',
        cpf: this.staffComplete.Staff.CPF || '',
        isForeigner: this.staffComplete.Staff.IsForeigner ?? false,
        passportNumber: this.staffComplete.Staff.PassportNumber || '',
        rg: this.staffComplete.Staff.RG || '',
        rgIssuingAuthority: this.staffComplete.Staff.RGIssuingAuthority || '',
        rgIssueDate: this.staffComplete.Staff.RGIssueDate || '',
        birthDate: this.staffComplete.Staff.BirthDate || '',
        gender: this.staffComplete.Staff.Gender || 'M',
        maritalStatus: this.staffComplete.Staff.MaritalStatus || 'Solteiro',
        motherName: this.staffComplete.Staff.MotherName || '',
        fatherName: this.staffComplete.Staff.FatherName || '',
        hasDisability: this.staffComplete.Staff.HasDisability ?? false,
        nationality: this.staffComplete.Staff.Nationality || 'Brasileira',
        birthPlace: this.staffComplete.Staff.BirthPlace || '',
        shift: this.staffComplete.Staff.Shift || 'Diurno',
        workSchedule: this.staffComplete.Staff.WorkSchedule || '08:00-16:00',
        unit: this.staffComplete.Staff.Unit || '',
        costCenter: this.staffComplete.Staff.CostCenter || null,
        costCenterId: (this.staffComplete.Staff as any).CostCenterId || null,
        bankName: this.staffComplete.Staff.BankName || '',
        agency: this.staffComplete.Staff.Agency || '',
        accountType: this.staffComplete.Staff.AccountType || '',
        accountNumber: this.staffComplete.Staff.AccountNumber || '',
        accountHolderName: this.staffComplete.Staff.AccountHolderName || '',
        pixKey: this.staffComplete.Staff.PIXKey || ''
      } as any,
      contract: {
        contractType: this.staffComplete.Staff.ContractType || 'CLT',
        startDate: this.staffComplete.Staff.HireDate || '',
        endDate: this.staffComplete.Staff.TerminationDate || null,
        notes: null,
        cnpj: this.staffComplete.Staff.CNPJ || '',
        companyName: this.staffComplete.Staff.CompanyName || '',
        tradeName: this.staffComplete.Staff.TradeName || '',
        companyAddress: this.staffComplete.Staff.CompanyAddress || '',
        stateRegistration: this.staffComplete.Staff.StateRegistration || '',
        municipalRegistration: this.staffComplete.Staff.MunicipalRegistration || '',
        companyEmail: this.staffComplete.Staff.CompanyEmail || '',
        companyPhone: this.staffComplete.Staff.CompanyPhone || '',
        costCenter: this.staffComplete.Staff.CostCenter || null
      },
      contacts: this.staffComplete.Contacts && this.staffComplete.Contacts.length > 0 ? 
        this.staffComplete.Contacts.map(contact => ({
          contactType: contact.ContactType || 'Telefone',
          contactName: contact.ContactName || '',
          phoneNumber: contact.PhoneNumber || '',
          relationship: contact.Relationship || 'Próprio',
          isEmergencyContact: contact.IsEmergencyContact ?? false,
          isDefault: contact.IsDefault ?? false
        })) : [],
      addresses: this.staffComplete.Addresses && this.staffComplete.Addresses.length > 0 ? 
        this.staffComplete.Addresses.map(address => ({
          addressType: address.AddressType || 'Residencial',
          street: address.Street || '',
          number: address.Number || '',
          complement: address.Complement || '',
          neighborhood: address.Neighborhood || '',
          city: address.City || '',
          state: address.State || '',
          zipCode: address.ZipCode || '',
          country: address.Country || 'Brasil',
          isDefault: address.IsDefault ?? false
        })) : [],
      salary: (() => {
        const salaryHistory = this.staffComplete?.SalaryHistory;
        if (salaryHistory && Array.isArray(salaryHistory) && salaryHistory.length > 0) {
          // Sempre usar o salário atual (IsCurrent) como referência.
          // Se não houver flag, usar o mais recente por EffectiveDate.
          const current =
            salaryHistory.find(s => (s as any).IsCurrent) ||
            [...salaryHistory].sort((a: any, b: any) => {
              const aDate = a.EffectiveDate ? new Date(a.EffectiveDate).getTime() : 0;
              const bDate = b.EffectiveDate ? new Date(b.EffectiveDate).getTime() : 0;
              return bDate - aDate;
            })[0];

          return {
            baseSalary: (current as any)?.BaseSalary ?? 0,
            grossSalary: (current as any)?.GrossSalary ?? 0,
            netSalary: (current as any)?.NetSalary ?? 0,
            currency: (current as any)?.Currency || 'BRL',
            salaryType: (current as any)?.SalaryType || 'Mensal',
            notes: (current as any)?.Notes || ''
          };
        }
        return {
          baseSalary: 0,
          grossSalary: 0,
          netSalary: 0,
          currency: 'BRL',
          salaryType: 'Mensal',
          notes: ''
        };
      })(),
      benefits: (() => {
        const benefits = this.staffComplete?.Benefits;
        if (benefits && Array.isArray(benefits) && benefits.length > 0) {
          return benefits.map(benefit => ({
            benefitType: benefit?.BenefitType || '',
            amount: benefit?.Amount ?? 0,
            currency: benefit?.Currency || 'BRL',
            startDate: benefit?.StartDate || '',
            isActive: benefit?.IsActive ?? true
          }));
        }
        return [];
      })(),
      attachments: this.staffComplete.Attachments && this.staffComplete.Attachments.length > 0 ?
        this.staffComplete.Attachments.map(attachment => ({
          id: attachment.Id,
          staffId: attachment.StaffId,
          documentName: attachment.DocumentName || '',
          status: attachment.Status || 'Pendente',
          filePath: attachment.FilePath || '',
          fileExtension: attachment.FileExtension || '',
          mimeType: attachment.MimeType || '',
          fileSize: attachment.FileSize || 0
        })) : []
    };

    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // Aguardar um ciclo para garantir que o Angular processou as mudanças
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 0);
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 100);
  }

  // Atualizar abas disponíveis baseado nos dados
  updateAvailableTabs(): void {
    if (!this.staffComplete) return;
    this.availableTabs.forEach(tab => {
      switch (tab.id) {
        case 'basic':
          tab.completed = true; // Sempre tem dados básicos
          break;
        case 'contract':
          tab.completed = true; // Sempre tem dados de contrato
          break;
                 case 'banking':
           // Verificar se tem dados bancários (estão dentro do objeto Staff)
           const hasBankingData = Boolean(
             (this.staffComplete!.Staff.BankName && this.staffComplete!.Staff.BankName.trim() !== '') || 
             (this.staffComplete!.Staff.Agency && this.staffComplete!.Staff.Agency.trim() !== '') || 
             (this.staffComplete!.Staff.AccountNumber && this.staffComplete!.Staff.AccountNumber.trim() !== '')
           );
           tab.completed = hasBankingData;
           break;
        case 'contacts':
          tab.completed = this.staffComplete!.Contacts && this.staffComplete!.Contacts.length > 0;
          break;
        case 'addresses':
          tab.completed = this.staffComplete!.Addresses && this.staffComplete!.Addresses.length > 0;
          break;
        case 'salary':
          tab.completed = this.staffComplete!.SalaryHistory && this.staffComplete!.SalaryHistory.length > 0;
          break;
        case 'benefits':
          tab.completed = this.staffComplete!.Benefits && this.staffComplete!.Benefits.length > 0;
          break;
        case 'documents':
          tab.completed = this.staffComplete!.Attachments && this.staffComplete!.Attachments.length > 0;
          break;
      }
    });
  }

  // Selecionar aba
  selectTab(tabId: string): void {
    this.activeTab = tabId;
    
    // Se a aba estiver vazia e o usuário estiver editando, inicializar dados
    if (this.isEditing && !this.isTabCompleted(tabId) && this.editData) {
      this.initializeEmptyData(tabId);
    }
    
    // Forçar detecção de mudanças ao trocar de aba
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // Aguardar um ciclo e forçar novamente para garantir atualização
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 0);
    
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 100);
  }

  // Iniciar edição
  startEditing(): void {
    if (!this.staffComplete) {
      console.error('❌ staffComplete não está disponível para iniciar edição');
      // Tentar recarregar os dados
      const staffId = this.route.snapshot.params['id'];
      if (staffId) {
        this.loadStaffComplete(staffId);
        return;
      }
      return;
    }

    this.mapEnumValuesFromDatabase();

    // Preparar dados para edição
    this.editData = {
      staff: {
        fullName: this.staffComplete.Staff.FullName || '',
        registration: this.staffComplete.Staff.Registration || '',
        position: this.staffComplete.Staff.Position || '',
        positionId: this.staffComplete.Staff.PositionId || null,
        department: this.staffComplete.Staff.Department || '',
        departmentId: this.staffComplete.Staff.DepartmentId || null,
        manager: this.staffComplete.Staff.Manager || '',
        managerId: (this.staffComplete.Staff as any).ManagerId || null,
        hireDate: this.staffComplete.Staff.HireDate || '',
        contractType: this.staffComplete.Staff.ContractType || 'CLT',
        cpf: this.staffComplete.Staff.CPF || '',
        isForeigner: this.staffComplete.Staff.IsForeigner ?? false,
        passportNumber: this.staffComplete.Staff.PassportNumber || '',
        rg: this.staffComplete.Staff.RG || '',
        rgIssuingAuthority: this.staffComplete.Staff.RGIssuingAuthority || '',
        rgIssueDate: this.staffComplete.Staff.RGIssueDate || '',
        birthDate: this.staffComplete.Staff.BirthDate || '',
        gender: this.staffComplete.Staff.Gender || 'M',
        maritalStatus: this.staffComplete.Staff.MaritalStatus || 'Solteiro',
        motherName: this.staffComplete.Staff.MotherName || '',
        fatherName: this.staffComplete.Staff.FatherName || '',
        hasDisability: this.staffComplete.Staff.HasDisability ?? false,
        nationality: this.staffComplete.Staff.Nationality || 'Brasileira',
        birthPlace: this.staffComplete.Staff.BirthPlace || '',
                 shift: this.staffComplete.Staff.Shift || 'Diurno',
         workSchedule: this.staffComplete.Staff.WorkSchedule || '08:00-16:00',
         costCenterId: (this.staffComplete.Staff as any).CostCenterId || null,
         // Dados bancários vão dentro do objeto staff - pegar primeiro do banco
        bankName: this.staffComplete.Staff.BankName || '',
        agency: this.staffComplete.Staff.Agency || '',
        accountType: this.staffComplete.Staff.AccountType || '',
        accountNumber: this.staffComplete.Staff.AccountNumber || '',
        accountHolderName: this.staffComplete.Staff.AccountHolderName || '',
        pixKey: this.staffComplete.Staff.PIXKey || ''
       } as any,
      contract: {
        contractType: this.staffComplete.Staff.ContractType || 'CLT',
        startDate: this.staffComplete.Staff.HireDate || '',
        endDate: this.staffComplete.Staff.TerminationDate || null,
        notes: null,
        // Campos PJ
        cnpj: this.staffComplete.Staff.CNPJ || '',
        companyName: this.staffComplete.Staff.CompanyName || '',
        tradeName: this.staffComplete.Staff.TradeName || '',
        companyAddress: this.staffComplete.Staff.CompanyAddress || '',
        stateRegistration: this.staffComplete.Staff.StateRegistration || '',
        municipalRegistration: this.staffComplete.Staff.MunicipalRegistration || '',
        companyEmail: this.staffComplete.Staff.CompanyEmail || '',
        companyPhone: this.staffComplete.Staff.CompanyPhone || '',
        // Manter centro de custo sempre disponível na edição (inclusive em PJ)
        costCenter: this.staffComplete.Staff.CostCenter || null
      },
                    // Dados bancários vão dentro do objeto staff
       contacts: this.staffComplete.Contacts && this.staffComplete.Contacts.length > 0 ? 
        this.staffComplete.Contacts.map(contact => ({
          // Pegar primeiro do banco, depois usar valores padrão se necessário
          contactType: contact.ContactType || 'Telefone',
          contactName: contact.ContactName || '',
          phoneNumber: contact.PhoneNumber || '',
          relationship: contact.Relationship || 'Próprio',
          isEmergencyContact: contact.IsEmergencyContact ?? false,
          isDefault: contact.IsDefault ?? false
        })) : [],
      addresses: this.staffComplete.Addresses && this.staffComplete.Addresses.length > 0 ? 
        this.staffComplete.Addresses.map(address => ({
          // Pegar primeiro do banco, depois usar valores padrão se necessário
          id: address.Id,
          staffId: address.StaffId,
          addressType: address.AddressType || 'Residencial',
          street: address.Street || '',
          number: address.Number || '',
          complement: address.Complement || '',
          neighborhood: address.Neighborhood || '',
          city: address.City || '',
          state: address.State || '',
          zipCode: address.ZipCode || '',
          country: address.Country || 'Brasil',
          isDefault: address.IsDefault ?? false,
          createdAt: address.CreatedAt || null,
          updatedAt: address.UpdatedAt || null,
          createdBy: address.CreatedBy || null,
          updatedBy: address.UpdatedBy || null
        })) : [],
      salary: this.staffComplete.SalaryHistory && this.staffComplete.SalaryHistory.length > 0 ? (() => {
        const salaryHistory = this.staffComplete!.SalaryHistory;
        const current =
          salaryHistory.find(s => (s as any).IsCurrent) ||
          [...salaryHistory].sort((a: any, b: any) => {
            const aDate = a.EffectiveDate ? new Date(a.EffectiveDate).getTime() : 0;
            const bDate = b.EffectiveDate ? new Date(b.EffectiveDate).getTime() : 0;
            return bDate - aDate;
          })[0];

        return {
          baseSalary: (current as any)?.BaseSalary || 0,
          grossSalary: (current as any)?.GrossSalary || 0,
          netSalary: (current as any)?.NetSalary || 0,
          currency: (current as any)?.Currency || 'BRL',
          salaryType: (current as any)?.SalaryType || 'Mensal',
          notes: (current as any)?.Notes || ''
        };
      })() : {
        baseSalary: 0,
        grossSalary: 0,
        netSalary: 0,
        currency: 'BRL',
        salaryType: 'Mensal',
        notes: ''
      },
      benefits: this.staffComplete.Benefits && this.staffComplete.Benefits.length > 0 ?
        this.staffComplete.Benefits.map(benefit => ({
          benefitType: benefit.BenefitType || '',
          amount: benefit.Amount || 0,
          currency: benefit.Currency || 'BRL',
          startDate: benefit.StartDate || '',
          isActive: benefit.IsActive ?? true
        })) : [],
      attachments: this.staffComplete.Attachments && this.staffComplete.Attachments.length > 0 ? 
        this.staffComplete.Attachments.map(attachment => ({
          id: attachment.Id,
          staffId: attachment.StaffId,
          documentName: attachment.DocumentName || '',
          documentType: attachment.DocumentType || '',
          status: attachment.Status || 'Pendente',
          filePath: attachment.FilePath || '',
          fileExtension: attachment.FileExtension || '',
          mimeType: attachment.MimeType || '',
          fileSize: attachment.FileSize || 0
        })) : []
    };

    this.isEditing = true;
    
    // Log dos attachments e seus tipos detectados
    if (this.editData.attachments && this.editData.attachments.length > 0) {
      this.editData.attachments.forEach((att: any, i) => {
        const detectedType = this.getAttachmentDocumentType(att);
      });
      
      // Verificar quais tipos têm documentos
      this.DOCUMENT_TYPES.forEach(docType => {
        const hasDoc = this.hasExistingDocument(docType.value);
      });
    }
    
    // Mapear valores dos enums para exibição na edição
    this.mapEnumValuesForEditing();
    
    // Carregar departamentos para permitir seleção e atribuição automática de gerente
    this.loadDepartments();
    
      // Se já tem departamento carregado, inicializar cargo e gerente
      // Caso contrário, será inicializado quando os departamentos carregarem (no subscribe)
      if (this.departments.length > 0 && (this.editData as any).staff?.department) {
      this.initializeDepartmentAndPosition();
      } else if ((this.editData as any).staff?.department) {
      // Aguardar carregamento assíncrono
      const checkInterval = setInterval(() => {
        if (this.departments.length > 0) {
          clearInterval(checkInterval);
          this.initializeDepartmentAndPosition();
        }
      }, 100);
      // Timeout de segurança
      setTimeout(() => clearInterval(checkInterval), 5000);
    }
    
    // Atualizar status das abas
    this.updateAvailableTabs();
  }

  // Salvar alterações
  saveChanges(): void {
    if (!this.editData || !this.staffComplete) {
      this.error = 'Dados de edição não disponíveis';
      return;
    }

    this.saving = true;
    this.error = null;
    this.successMessage = null;

    const staffId = this.staffComplete.Staff.Id;
    
    
    // Validar attachments
    if (this.editData.attachments) {
      this.editData.attachments.forEach((att, i) => {
      });
    }
    
    // Validar estrutura do payload antes de enviar
    if (this.editData.staff) {
    }
    if (this.editData.contacts) {
    }
    if (this.editData.addresses) {
    }
         // Dados bancários estão dentro do objeto staff
     if (this.editData.staff) {
       if (this.editData.staff.bankName || this.editData.staff.agency || this.editData.staff.accountNumber) {
       }
     }
    
    // Verificar se o payload está de acordo com o modelo StaffEditPayload
    if (this.editData.staff) {
      const requiredStaffFields = ['fullName', 'registration', 'position', 'department', 'contractType', 'cpf', 'birthDate'];
      const staffData = this.editData.staff;
      const missingStaffFields = requiredStaffFields.filter(field => !staffData[field as keyof typeof staffData]);
      if (missingStaffFields.length > 0) {
      } else {
      }
    }

    // Garantir que os campos de PJ (empresa) sejam enviados no objeto staff, não só em contract
    this.syncCompanyFieldsToStaff();

    const performUpdate = () => {
      // Garantir campos de empresa (PJ) no objeto staff
      this.syncCompanyFieldsToStaff();
      
      // NÃO enviar attachments ao salvar - o upload já foi feito manualmente
      // Criar cópia do editData sem os attachments
      const dataToSend = {
        ...this.editData!,
        attachments: [] // Não enviar attachments, já foram salvos via upload manual
      };
      

      this.staffService.updateStaffComplete(staffId, dataToSend).subscribe({
      next: (updatedStaff) => {
        
        // IMPORTANTE: Verificar o formato da resposta da API
        
        // A API pode retornar tanto 'Staff' (maiúsculo) quanto 'staff' (minúsculo)
        const staffData = (updatedStaff as any).Staff || (updatedStaff as any).staff;
        const contactsData = (updatedStaff as any).Contacts || (updatedStaff as any).contacts;
        const addressesData = (updatedStaff as any).Addresses || (updatedStaff as any).addresses;
        
        if (staffData) {
        } else {
          console.error('❌ Formato de resposta inesperado da API:', updatedStaff);
        }
        
        // IMPORTANTE: Recarregar dados frescos do backend
        this.loadStaffComplete(staffId);
        
        // IMPORTANTE: Verificar se os dados foram modificados
        if (this.staffComplete && this.editData?.staff) {
          const originalStaff = this.staffComplete.Staff;
          const modifiedStaff = this.editData.staff;
          
          const changedFields: Array<{field: string, original: any, modified: any}> = [];
          Object.keys(modifiedStaff).forEach(key => {
            const originalValue = (originalStaff as any)[key];
            const modifiedValue = (modifiedStaff as any)[key];
            if (originalValue !== modifiedValue) {
              changedFields.push({
                field: key,
                original: originalValue,
                modified: modifiedValue
              });
            }
          });
          
          if (changedFields.length > 0) {
          } else {
          }
        }

        // IMPORTANTE: Testar serialização do payload
        try {
          const serializedPayload = JSON.stringify(this.editData);
          
          // Testar deserialização
          const deserializedPayload = JSON.parse(serializedPayload);
          
          // Verificar se a estrutura foi mantida
          if (deserializedPayload.staff && deserializedPayload.staff.fullName) {
          } else {
            console.error('❌ Estrutura do payload perdida após serialização');
          }
        } catch (error) {
          console.error('❌ Erro na serialização do payload:', error);
        }

        // Sair do modo de edição
        this.isEditing = false;
        this.editData = null;
        
        // Limpar documentos "outros" pendentes
        this.otherDocuments = [];
        
        // Limpar status de upload
        this.uploadedDocuments = {};
        this.uploadStatus = {};
        this.uploadProgress = {};
        
        // Mostrar mensagem de sucesso
        this.successMessage = 'Funcionário atualizado com sucesso!';
        
        // Recarregar os dados do funcionário para mostrar a visualização atualizada
        this.loadStaffComplete(staffId);
        
        // Limpar mensagem após 3 segundos
        setTimeout(() => {
          this.successMessage = null;
        }, 3000);
        
        this.saving = false;
      },
      error: (error) => {
        console.error('❌ Erro ao salvar alterações:', error);
        
        if (error.status === 400) {
          this.error = 'Dados inválidos. Verifique as informações e tente novamente.';
        } else if (error.status === 404) {
          this.error = 'Funcionário não encontrado.';
        } else if (error.status === 500) {
          this.error = 'Erro interno do servidor. Tente novamente mais tarde.';
        } else {
          this.error = error.error?.message || error.message || 'Erro ao salvar alterações';
        }
        
        this.saving = false;
      }
      });
    };

    // Carregar departamentos e replicar a lógica de atribuição automática de gerente
    const staffDeptName = this.editData.staff?.department;
    if (staffDeptName) {
      this.loadDepartmentsAndApplyManager(staffDeptName, performUpdate);
    } else {
      performUpdate();
    }
  }

  /**
   * Garante que os campos de empresa (PJ) estejam no objeto staff antes de enviar.
   * O backend espera esses campos dentro de staff, não apenas em contract.
   */
  private syncCompanyFieldsToStaff(): void {
    const staff = this.editData?.staff;
    const contract = this.editData?.contract;
    if (!staff || !contract) return;

    const isPJ = (contract.contractType || staff.contractType) === 'PJ';
    const companyFields = [
      'cnpj',
      'companyName',
      'tradeName',
      'companyAddress',
      'stateRegistration',
      'municipalRegistration',
      'companyEmail',
      'companyPhone'
    ];

    companyFields.forEach(field => {
      if (isPJ) {
        (staff as any)[field] = (contract as any)[field] ?? null;
      } else {
        (staff as any)[field] = null;
      }
    });

    // Garantir que o centro de custo seja refletido em staff (CostCenter)
    (staff as any).costCenter = (contract as any).costCenter ?? null;

    const selectedDepartment =
      this.departments.find(d => d.id === (staff as any).departmentId)
      || this.departments.find(d => d.name === (staff as any).department);
    (staff as any).departmentId = selectedDepartment?.id ?? (staff as any).departmentId ?? null;
    (staff as any).managerId = selectedDepartment?.managerStaffId ?? (staff as any).managerId ?? null;

    if ((staff as any).position && this.positions.length > 0) {
      const selectedPosition = this.positions.find(p => p.name === (staff as any).position);
      (staff as any).positionId = selectedPosition?.id ?? (staff as any).positionId ?? null;
    } else {
      (staff as any).positionId = (staff as any).positionId ?? null;
    }

    const selectedCostCenterValue = (contract as any).costCenter;
    const selectedCostCenter = this.resolveCostCenterSelection(selectedCostCenterValue);
    (staff as any).costCenter = selectedCostCenter?.code ?? selectedCostCenterValue ?? null;
    (staff as any).costCenterId = selectedCostCenter?.id ?? (staff as any).costCenterId ?? null;
    // Garantia extra para backends que processam payload em PascalCase
    (staff as any).CostCenterId = (staff as any).costCenterId;
  }

  private resolveCostCenterSelection(selected: any): CostCenter | null {
    if (selected == null || selected === '') {
      return null;
    }

    const selectedAsText = String(selected).trim();
    const byCode = this.costCenters.find(cc => (cc.code || '').trim() === selectedAsText);
    if (byCode) {
      return byCode;
    }

    const byIdText = this.costCenters.find(cc => String(cc.id) === selectedAsText);
    if (byIdText) {
      return byIdText;
    }

    if (typeof selected === 'number') {
      return this.costCenters.find(cc => cc.id === selected) ?? null;
    }

    return null;
  }

  // Carregar departamentos
  loadDepartments(): void {
    if (this.departments.length > 0) {
      return; // Já carregado
    }
    this.isLoadingDepartments = true;
    this.departmentService.getAllDepartments().subscribe({
      next: (list) => {
        this.departments = list || [];
        this.isLoadingDepartments = false;
        // Se estiver em modo de edição e já tiver departamento, inicializar cargo e gerente
        if (this.isEditing && (this.editData as any)?.staff?.department) {
          this.initializeDepartmentAndPosition();
        }
      },
      error: (err) => {
        console.error('Erro ao carregar departamentos:', err);
        this.isLoadingDepartments = false;
      }
    });
  }

  // Quando selecionar um departamento (replicar lógica do cadastro)
  onDepartmentChange(departmentName: string): void {
    if (!this.editData?.staff) return;

    // Atualiza o valor selecionado
    (this.editData.staff as any).department = departmentName;

    const department = this.departments.find(d => d.name === departmentName);
    if (department) {
      this.selectedDepartmentId = department.id;
      (this.editData.staff as any).departmentId = department.id;
      (this.editData.staff as any).managerId = department.managerStaffId ?? null;
      // Preencher o gerente automaticamente
      if (department.managerStaffName) {
        (this.editData.staff as any).manager = department.managerStaffName;
      } else {
        (this.editData.staff as any).manager = null;
      }
      // Carregar cargos do departamento
      this.loadPositionsByDepartment(department.id);
    } else {
      this.selectedDepartmentId = null;
      (this.editData.staff as any).managerId = null;
      (this.editData.staff as any).manager = null;
      this.positions = [];
    }
  }

  // Carregar cargos por departamento
  loadPositionsByDepartment(departmentId: number): void {
    this.isLoadingPositions = true;
    this.positions = [];
    this.positionService.getPositionsByDepartment(departmentId).subscribe({
      next: (response) => {
        this.positions = response.positions || [];
        this.isLoadingPositions = false;
        // Limpar cargo selecionado se não estiver mais na lista
        if (this.editData?.staff?.position && !this.positions.find(p => p.name === this.editData!.staff!.position)) {
          (this.editData.staff as any).position = '';
        }
      },
      error: (err) => {
        console.error('Erro ao carregar cargos:', err);
        this.positions = [];
        this.isLoadingPositions = false;
      }
    });
  }

  /**
   * Carrega departamentos (se necessário) e aplica a mesma lógica de atribuição automática
   * de gerente usada no cadastro: ao definir o departamento, usar o managerStaffName.
   */
  private loadDepartmentsAndApplyManager(departmentName: string, onReady: () => void): void {
    if (this.departments.length > 0) {
      this.applyManagerFromDepartment(departmentName);
      onReady();
      return;
    }

    this.isLoadingDepartments = true;
    this.departmentService.getAllDepartments().subscribe({
      next: (list) => {
        this.departments = list || [];
        this.isLoadingDepartments = false;
        this.applyManagerFromDepartment(departmentName);
        onReady();
      },
      error: (err) => {
        console.error('Erro ao carregar departamentos para atualização de staff:', err);
        this.isLoadingDepartments = false;
        // Mesmo com erro, seguir com o update para não travar o fluxo
        onReady();
      }
    });
  }

  /**
   * Inicializa departamento e cargo quando iniciar edição (se já existirem).
   */
  private initializeDepartmentAndPosition(): void {
    const staffAny = (this.editData as any)?.staff || {};
    if ((!staffAny.department && !staffAny.departmentId) || this.departments.length === 0) return;

    let dept: Department | undefined;
    if (staffAny.departmentId) {
      dept = this.departments.find(d => d.id === staffAny.departmentId);
    }
    if (!dept && staffAny.department) {
      dept = this.departments.find(d => d.name === staffAny.department);
    }

    if (dept) {
      this.selectedDepartmentId = dept.id;
      staffAny.department = dept.name;
      staffAny.departmentId = dept.id;
      this.loadPositionsByDepartment(dept.id);
      // Aplicar gerente se o departamento tiver
      if (dept.managerStaffName) {
        staffAny.manager = dept.managerStaffName;
      }
    }
  }

  /**
   * Aplica o gerente automaticamente com base no nome do departamento (se encontrado).
   */
  private applyManagerFromDepartment(departmentName: string): void {
    if (!this.editData?.staff || !departmentName || !this.departments.length) return;

    const department = this.departments.find(d => d.name === departmentName);
    if (department) {
      // Replicar lógica do cadastro: se departamento tem gerente configurado, usar esse valor
      (this.editData.staff as any).manager = department.managerStaffName || null;
    }
  }

  // Recarregar dados do funcionário
  private loadStaffComplete(id: number): void {
    this.loading = true;
    this.error = null;
    
    
    this.staffService.getStaffComplete(id).subscribe({
      next: (data) => {
        
        this.staffComplete = data;
        this.loading = false;
        
        // Mapear valores dos enums do banco de dados
        this.mapEnumValuesFromDatabase();
        
        this.updateAvailableTabs();
        
        // Verificar se os dados foram atualizados corretamente
        this.verifyDataUpdate();
      },
      error: (error) => {
        console.error('❌ Erro ao recarregar dados:', error);
        this.error = 'Erro ao recarregar dados do funcionário';
        this.loading = false;
      }
    });
  }

  // Verificar se os dados foram atualizados corretamente
  private verifyDataUpdate(): void {
    if (!this.staffComplete) return;
    
    
    // Verificar dados básicos
    if (this.staffComplete.Staff.FullName) {
    }
    
    // Verificar contatos
    if (this.staffComplete.Contacts && this.staffComplete.Contacts.length > 0) {
    }
    
    // Verificar endereços
    if (this.staffComplete.Addresses && this.staffComplete.Addresses.length > 0) {
    }
    
    // Verificar dados bancários
    if (this.staffComplete.Staff.BankName || this.staffComplete.Staff.Agency || this.staffComplete.Staff.AccountNumber) {
    }
    
  }

  // Atualizar campo de edição
  updateField(section: string, field: string, value: any, index?: number): void {
    if (!this.editData) {
      return;
    }

    try {
      if (section === 'staff') {
        (this.editData.staff as any)[field] = value;
      } else if (section === 'contract' && this.editData.contract) {
        (this.editData.contract as any)[field] = value;
        
        // Se mudou o tipo de contrato, recalcular horário
        if (field === 'contractType') {
          const currentStartTime = this.getWorkStartTime();
          this.calculateWorkSchedule(currentStartTime);
        }
             } else if (section === 'bankingDetails' && index !== undefined) {
         // Dados bancários agora vão dentro do objeto staff (não mais usado)
      } else if (section === 'contacts' && index !== undefined) {
        if (!this.editData.contacts[index]) {
          return;
        }
        (this.editData.contacts[index] as any)[field] = value;
      } else if (section === 'addresses' && index !== undefined) {
        if (!this.editData.addresses[index]) {
          return;
        }
        (this.editData.addresses[index] as any)[field] = value;
      } else if (section === 'salary' && this.editData.salary) {
        (this.editData.salary as any)[field] = value;
      } else if (section === 'benefits' && index !== undefined) {
        if (!this.editData.benefits) {
          this.editData.benefits = [];
        }
        if (!this.editData.benefits[index]) {
          this.editData.benefits[index] = {
            benefitType: '',
            amount: 0,
            currency: 'BRL',
            startDate: '',
            isActive: true
          };
        }
        (this.editData.benefits[index] as any)[field] = value;
      } else if (section === 'attachments' && index !== undefined) {
        if (!this.editData.attachments[index]) {
          return;
        }
        (this.editData.attachments[index] as any)[field] = value;
      } else {
      }

      // Forçar detecção de mudanças para atualizar a view
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error(`❌ Erro ao atualizar campo ${section}.${field}:`, error);
    }
  }

  // Métodos helper para extrair valores dos eventos
  getRgIssueDateForInput(): string {
    if (this.editData?.staff?.rgIssueDate) {
      return this.editData.staff.rgIssueDate.split('T')[0];
    }
    return '';
  }

  getBirthDateForInput(): string {
    if (this.editData?.staff?.birthDate) {
      return this.editData.staff.birthDate.split('T')[0];
    }
    return '';
  }

  getContractStartDateForInput(): string {
    if (this.editData?.staff?.hireDate) {
      return this.editData.staff.hireDate.split('T')[0];
    }
    return '';
  }

  getCheckboxValue(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  getInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  getSelectValue(event: Event): string {
    return (event.target as HTMLSelectElement).value;
  }

  getTextareaValue(event: Event): string {
    return (event.target as HTMLTextAreaElement).value;
  }

  parseFloatValue(value: string): number {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  // Métodos helper para dados do salário
  getCurrentSalary(): any {
    if (!this.staffComplete?.SalaryHistory) return null;
    return this.staffComplete.SalaryHistory.find(s => s.IsCurrent) || null;
  }

  getCurrentSalaryBase(): number {
    const current = this.getCurrentSalary();
    return current?.BaseSalary || 0;
  }

  getCurrentSalaryGross(): number {
    const current = this.getCurrentSalary();
    return current?.GrossSalary || 0;
  }

  getCurrentSalaryNet(): number {
    const current = this.getCurrentSalary();
    return current?.NetSalary || 0;
  }

  getCurrentSalaryNotes(): string {
    const current = this.getCurrentSalary();
    return current?.Notes || '-';
  }

  // Método helper para formatar data de fim do contrato
  getContractEndDateForInput(): string {
    if (!this.editData?.contract?.endDate) return '';
    return this.editData.contract.endDate.split('T')[0];
  }

  // Inicializar dados vazios para uma aba
  initializeEmptyData(tabId: string): void {
    if (!this.editData) return;

    switch (tabId) {
             case 'banking':
         // Dados bancários são únicos e já estão inicializados no objeto staff
         break;
      case 'contacts':
        if (!this.editData.contacts || this.editData.contacts.length === 0) {
          this.editData.contacts = [{
            contactType: 'Telefone',
            contactName: '',
            phoneNumber: '',
            relationship: 'Próprio',
            isEmergencyContact: false,
            isDefault: true
          }];
        }
        break;
      case 'addresses':
        if (!this.editData.addresses || this.editData.addresses.length === 0) {
          this.editData.addresses = [{
            addressType: 'Residencial',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'Brasil',
            isDefault: true
          }];
        }
        break;
      case 'documents':
        if (!this.editData.attachments || this.editData.attachments.length === 0) {
          this.editData.attachments = [{
            documentName: '',
            status: 'Pendente',
            filePath: '',
            fileExtension: '',
            mimeType: '',
            fileSize: 0
          }];
        }
        break;
    }

  }

  // Adicionar novo item para uma aba
  addNewItem(tabId: string): void {
    if (!this.editData) return;

    switch (tabId) {
      case 'contacts':
        this.editData.contacts.push({
          contactType: 'Telefone',
          contactName: '',
          phoneNumber: '',
          relationship: 'Próprio',
          isEmergencyContact: false,
          isDefault: false
        });
        break;
      case 'addresses':
        this.editData.addresses.push({
          addressType: 'Residencial',
          street: '',
          number: '',
          complement: '',
          neighborhood: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'Brasil',
          isDefault: false
        });
        break;
             case 'banking':
         // Dados bancários são únicos e vão dentro do objeto staff
         // Não é necessário adicionar novos itens
         break;
    }

  }

  // Remover item de uma aba
  removeItem(tabId: string, index: number): void {
    if (!this.editData) return;

    switch (tabId) {
      case 'contacts':
        if (this.editData.contacts.length > 1) {
          this.editData.contacts.splice(index, 1);
        }
        break;
      case 'addresses':
        if (this.editData.addresses.length > 1) {
          this.editData.addresses.splice(index, 1);
        }
        break;
             case 'banking':
         // Dados bancários são únicos e não podem ser removidos
         break;
    }

  }

  // Remover anexo existente
  removeAttachment(index: number): void {
    if (!this.editData?.attachments || index < 0 || index >= this.editData.attachments.length) {
      return;
    }

    const attachment = this.editData.attachments[index] as any;
    
    const staffId = this.staffComplete?.Staff?.Id;
    const filePath = attachment.filePath || attachment.FilePath || '';
    
    // Extrair o fileName do filePath (última parte da URL)
    let fileName = '';
    if (filePath) {
      // Se for uma URL completa, pegar a última parte
      const parts = filePath.split('/');
      fileName = parts[parts.length - 1];
      // Remover query strings se houver
      fileName = fileName.split('?')[0];
    }
    
    
    // Se tem staffId e fileName, chamar API para excluir do blob
    if (staffId && fileName) {
      this.staffService.deleteDocument(staffId.toString(), fileName).subscribe({
        next: (response) => {
          
          // Remover da lista local
          this.editData!.attachments.splice(index, 1);
          
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('❌ Erro ao excluir documento do blob:', error);
          
          // Mesmo com erro, remover da lista local (pode ser que o arquivo já não exista)
          this.editData!.attachments.splice(index, 1);
          
          this.cdr.detectChanges();
        }
      });
    } else {
      // Se não tem fileName (documento novo que ainda não foi salvo), apenas remove da lista
      this.editData.attachments.splice(index, 1);
    }
  }

  // Métodos helper para verificar estado das abas
  isTabCompleted(tabId: string): boolean {
    const tab = this.availableTabs.find(t => t.id === tabId);
    return tab ? tab.completed : false;
  }

  isTabRequired(tabId: string): boolean {
    const tab = this.availableTabs.find(t => t.id === tabId);
    return tab ? tab.required : false;
  }

  // Navegação entre abas
  goToPreviousTab(): void {
    const currentIndex = this.availableTabs.findIndex(tab => tab.id === this.activeTab);
    if (currentIndex > 0) {
      this.selectTab(this.availableTabs[currentIndex - 1].id);
    }
  }

  goToNextTab(): void {
    const currentIndex = this.availableTabs.findIndex(tab => tab.id === this.activeTab);
    if (currentIndex < this.availableTabs.length - 1) {
      this.selectTab(this.availableTabs[currentIndex + 1].id);
    }
  }

  isFirstTab(): boolean {
    return this.activeTab === this.availableTabs[0].id;
  }

  isLastTab(): boolean {
    return this.activeTab === this.availableTabs[this.availableTabs.length - 1].id;
  }

  isTabValid(tabId: string): boolean {
    // Implementar validação específica para cada aba se necessário
    return true;
  }

  // Formatar data para exibição
  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  // Normalizar status do documento para exibição
  normalizeDocumentStatus(status: string | null | undefined): string {
    if (!status) return 'Pendente';
    
    const statusLower = status.toLowerCase();
    
    // Mapear variações para o status padrão "Aprovado"
    if (statusLower === 'ativo' || statusLower === 'active' || statusLower === 'aprovado' || statusLower === 'approved') {
      return 'Aprovado';
    }
    
    if (statusLower === 'pendente' || statusLower === 'pending') {
      return 'Pendente';
    }
    
    if (statusLower === 'rejeitado' || statusLower === 'rejected' || statusLower === 'reprovado') {
      return 'Rejeitado';
    }
    
    // Retorna o status original com primeira letra maiúscula
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  // Formatar tamanho do arquivo
  formatFileSize(bytes: number | null | undefined): string {
    if (!bytes || bytes === 0) return '-';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let size = bytes;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  // Formatar valor monetário
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  // Formatar status
  formatStatus(inactive: boolean): string {
    return inactive ? 'Inativo' : 'Ativo';
  }

  // Métodos para gerenciar turno e horário
  onShiftChange(shift: string): void {
    if (!this.editData) return;
    
    // Atualizar o turno
    this.updateField('staff', 'shift', shift);
    
    // Sugerir horário baseado no turno
    let suggestedStartTime = '';
    switch (shift) {
      case 'Diurno':
        suggestedStartTime = '08:00';
        break;
      case 'Noturno':
        suggestedStartTime = '22:00';
        break;
      case 'Integral':
        suggestedStartTime = '08:00';
        break;
    }
    
    if (suggestedStartTime) {
      this.updateField('staff', 'workSchedule', suggestedStartTime);
      this.calculateWorkSchedule(suggestedStartTime);
    }
  }

  onWorkStartChange(startTime: string): void {
    if (!this.editData) return;
    
    
    // Atualizar o horário de início
    this.updateField('staff', 'workSchedule', startTime);
    
    // Detectar turno automaticamente
    const detectedShift = this.detectShiftFromTime(startTime);
    this.updateField('staff', 'shift', detectedShift);
    
    // Calcular horário de saída automaticamente
    this.calculateWorkSchedule(startTime);
    
    // Forçar atualização da view
    setTimeout(() => {
      this.calculateWorkSchedule(startTime);
      this.cdr.detectChanges(); // Forçar detecção de mudanças
    }, 100);
    
  }

  // Detectar turno baseado no horário de entrada
  detectShiftFromTime(startTime: string): string {
    const startHour = parseInt(startTime);
    
    if (startHour >= 6 && startHour < 18) {
      return 'Diurno';
    } else {
      return 'Noturno';
    }
  }

  // Calcular horário de trabalho baseado no tipo de contrato
  calculateWorkSchedule(startTime: string): void {
    if (!this.editData) return;
    
    const contractType = this.editData.contract?.contractType || 'CLT';
    const startHour = parseInt(startTime);
    
    
    // Determinar duração baseada no tipo de contrato e horário
    let workHours = 8; // CLT e PJ padrão
    let lunchHours = 1; // Hora de almoço padrão
    
    if (contractType === 'Estagiário') {
      workHours = 6;
      lunchHours = 0; // Estagiário não tem hora de almoço
    }
    
    // Detectar turno e ajustar hora de almoço
    const isNightShift = startHour >= 18 || startHour <= 5;
    
    if (isNightShift) {
      lunchHours = 0; // Turno noturno: sem hora de almoço
    } else {
    }
    
    const totalHours = workHours + lunchHours;
    
    // Calcular horário de saída
    let endHour = startHour + totalHours;
    let nextDay = false;
    
    // Ajustar para o próximo dia se necessário
    if (endHour >= 24) {
      endHour = endHour - 24;
      nextDay = true;
    }
    
    // Formatar horário de saída
    const endTime = endHour.toString().padStart(2, '0') + ':00';
    
    // Atualizar o horário completo (início-fim)
    const fullSchedule = `${startTime}-${endTime}`;
    this.updateField('staff', 'workSchedule', fullSchedule);
    
    // Log detalhado
    const lunchInfo = lunchHours > 0 ? ` + ${lunchHours}h almoço` : ' (sem almoço)';
    const dayInfo = nextDay ? ' (próximo dia)' : '';
    
    // Atualizar turno se necessário
    const detectedShift = this.detectShiftFromTime(startTime);
    if (this.editData.staff.shift !== detectedShift) {
      this.updateField('staff', 'shift', detectedShift);
    }
  }

  // Métodos helper para exibir horários
  getWorkStartTime(): string {
    // Se estiver editando, usar dados de edição
    if (this.isEditing && this.editData?.staff?.workSchedule) {
      const schedule = this.editData.staff.workSchedule;
      return schedule.includes('-') ? schedule.split('-')[0] : schedule;
    }
    
    // Caso contrário, usar dados originais
    if (!this.staffComplete?.Staff.WorkSchedule) return '08:00';
    const schedule = this.staffComplete.Staff.WorkSchedule;
    return schedule.includes('-') ? schedule.split('-')[0] : schedule;
  }

  getWorkEndTime(): string {
    let schedule = '';
    let contractType = 'CLT';
    
    // Se estiver editando, usar dados de edição
    if (this.isEditing && this.editData?.staff?.workSchedule) {
      schedule = this.editData.staff.workSchedule;
      contractType = this.editData.contract?.contractType || 'CLT';
    } else {
      // Caso contrário, usar dados originais
      if (!this.staffComplete?.Staff.WorkSchedule) return '17:00'; // 08:00 + 9h = 17:00
      schedule = this.staffComplete.Staff.WorkSchedule;
      contractType = this.staffComplete?.Staff.ContractType || 'CLT';
    }
    
    if (schedule.includes('-')) {
      return schedule.split('-')[1];
    } else {
      // Se só tem horário de entrada, calcular saída
      const startTime = schedule;
      const startHour = parseInt(startTime);
      
      let totalHours = 9; // 8h trabalho + 1h almoço padrão
      if (contractType === 'Estagiário') {
        totalHours = 6; // Estagiário não tem hora de almoço
      } else if (startHour >= 18 || startHour <= 5) {
        totalHours = 8; // Turno noturno sem almoço
      }
      
      let endHour = startHour + totalHours;
      if (endHour >= 24) {
        endHour = endHour - 24;
      }
      
      return endHour.toString().padStart(2, '0') + ':00';
    }
  }

  getWorkHours(): number {
    if (!this.editData?.contract?.contractType) {
      // Se não tem tipo de contrato, assumir CLT com horário padrão
      const startTime = this.getWorkStartTime();
      const startHour = parseInt(startTime);
      return (startHour >= 18 || startHour <= 5) ? 8 : 9; // Noturno sem almoço, diurno com almoço
    }
    
    const contractType = this.editData.contract.contractType;
    const startTime = this.getWorkStartTime();
    const startHour = parseInt(startTime);
    
    if (contractType === 'Estagiário') {
      return 6; // Estagiário sempre 6h
    }
    
    // CLT/PJ: verificar se é turno noturno (18:00 - 5:59)
    return (startHour >= 18 || startHour <= 5) ? 8 : 9;
  }

  onWorkEndChange(newEndTime: string): void {
    const startTime = this.getWorkStartTime() || '08:00';
    const endTime = newEndTime || '17:00';
    if (this.editData && this.editData.staff) {
      this.editData.staff.workSchedule = `${startTime}-${endTime}`;
    }
  }

  loadCostCenters(): void {
    this.isLoadingCostCenters = true;
    this.costCentersService.getAll({ page: 1, pageSize: 50 }).subscribe({
      next: (result) => {
        this.costCenters = result.items || [];
        this.isLoadingCostCenters = false;
      },
      error: () => {
        this.isLoadingCostCenters = false;
      }
    });
  }

  getCostCenterDisplay(): string {
    const code = this.editData?.contract?.costCenter || this.staffComplete?.Staff?.CostCenter || '';
    if (!code) return '-';
    const match = this.costCenters.find(cc => cc.code === code);
    if (match) {
      return `${match.code} - ${match.name}`;
    }
    return code;
  }

  // Métodos para upload de documentos
  onFileSelected(event: any, docType: string): void {
    const file = event.target.files[0];
    if (file) {
      this.uploadedDocuments[docType] = {
        file: file,
        uploaded: false,
        preview: docType === 'photo' ? URL.createObjectURL(file) : null
      };
      this.uploadStatus[docType] = 'pending';
    }
  }

  uploadDocumentToBackend(docType: string): void {
    const staffId = this.staffComplete?.Staff?.Id;
    const docData = this.uploadedDocuments[docType];
    
    if (!staffId || !docData?.file) {
      console.error('❌ StaffId ou arquivo não disponível para upload');
      this.uploadStatus[docType] = 'error';
      return;
    }
    
    this.uploadStatus[docType] = 'uploading';
    this.uploadProgress[docType] = 0;
    
    // Encontrar o nome do documento baseado no tipo
    const docTypeConfig = this.DOCUMENT_TYPES.find(d => d.value === docType);
    const documentName = docTypeConfig?.label || docType;
    
    // Fazer upload real para a API
    this.staffService.uploadDocument(docData.file, staffId, docType.toUpperCase(), documentName)
      .subscribe({
        next: (response) => {
          
          this.uploadProgress[docType] = 100;
          this.uploadStatus[docType] = 'success';
          this.uploadedDocuments[docType].uploaded = true;
          this.uploadedDocuments[docType].response = response;
          
          // Extrair a URL do blob (verificar tanto camelCase quanto PascalCase)
          const blobUrl = response.url || response.Url || response.filePath || response.FilePath || '';
          
          // Adicionar o documento aos attachments do editData
          if (this.editData && response) {
            const newAttachment = {
              staffId: parseInt(response.staffId || response.StaffId) || staffId,
              documentName: response.documentName || response.DocumentName || documentName,
              documentType: docType,
              status: 'Aprovado',
              filePath: blobUrl,
              fileExtension: response.fileExtension || response.FileExtension || '',
              mimeType: this.getMimeType(response.fileExtension || response.FileExtension) || '',
              fileSize: response.fileSize || response.FileSize || docData.file.size
            };
            
            // Inicializar array se não existir
            if (!this.editData.attachments) {
              this.editData.attachments = [];
            }
            
            this.editData.attachments.push(newAttachment);
          }
          
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('❌ Erro no upload do documento:', error);
          this.uploadStatus[docType] = 'error';
          this.uploadProgress[docType] = 0;
          this.cdr.detectChanges();
        }
      });
  }
  
  // Helper para obter MIME type baseado na extensão
  private getMimeType(extension: string): string {
    const mimeTypes: { [key: string]: string } = {
      '.pdf': 'application/pdf',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.gif': 'image/gif',
      '.doc': 'application/msword',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.xls': 'application/vnd.ms-excel',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    };
    return mimeTypes[extension?.toLowerCase()] || 'application/octet-stream';
  }

  deleteDocument(docType: string): void {
    if (this.uploadedDocuments[docType]?.preview) {
      URL.revokeObjectURL(this.uploadedDocuments[docType].preview);
    }
    delete this.uploadedDocuments[docType];
    delete this.uploadStatus[docType];
    delete this.uploadProgress[docType];
  }

  getFileName(docType: string): string {
    return this.uploadedDocuments[docType]?.file?.name || '';
  }

  getFileSize(docType: string): string {
    const file = this.uploadedDocuments[docType]?.file;
    if (!file) return '';
    
    const sizeInBytes = file.size;
    if (sizeInBytes < 1024) return sizeInBytes + ' B';
    if (sizeInBytes < 1024 * 1024) return (sizeInBytes / 1024).toFixed(1) + ' KB';
    return (sizeInBytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  getUploadedDocumentsCount(): number {
    return Object.values(this.uploadedDocuments).filter(doc => doc.uploaded).length;
  }

  getPendingDocumentsCount(): number {
    return Object.values(this.uploadedDocuments).filter(doc => !doc.uploaded).length;
  }

  getDocumentElement(): any {
    return document;
  }

  // Método para voltar à lista de funcionários
  goBack(): void {
    this.router.navigate(['/hr/staff']);
  }

  // Forçar início da edição (usado quando o botão edit é clicado)
  forceStartEditing(): void {
    
    if (this.staffComplete) {
      this.startEditing();
    } else {
      // Se não há dados, tentar recarregar e depois iniciar edição
      const staffId = this.route.snapshot.params['id'];
      if (staffId) {
        this.loading = true;
        this.staffService.getStaffComplete(staffId).subscribe({
          next: (data) => {
            this.staffComplete = data;
            this.loading = false;
            this.startEditing();
          },
          error: (error) => {
            console.error('❌ Erro ao recarregar dados para edição:', error);
            this.loading = false;
            this.error = 'Erro ao carregar dados do funcionário para edição';
          }
        });
      }
    }
  }

  // Limpar mensagens
  clearMessages(): void {
    this.error = null;
    this.successMessage = null;
  }

  // Limpar erro
  clearError(): void {
    this.error = null;
  }

  // Limpar sucesso
  clearSuccess(): void {
    this.successMessage = null;
  }

  // Mapear valores dos enums para exibição
  mapEnumValue(field: string, value: string | null): string {
    if (!value) return '-';
    
    switch (field) {
      case 'gender':
        switch (value.toUpperCase()) {
          case 'M': return 'Masculino';
          case 'F': return 'Feminino';
          case 'N': return 'Não informado';
          case 'MASCULINO': return 'Masculino';
          case 'FEMININO': return 'Feminino';
          case 'NÃO INFORMADO': return 'Não informado';
          default: return value;
        }
      case 'maritalStatus':
        switch (value.toLowerCase()) {
          case 'solteiro': return 'Solteiro';
          case 'casado': return 'Casado';
          case 'divorciado': return 'Divorciado';
          case 'viúvo': return 'Viúvo';
          case 'viuvo': return 'Viúvo';
          default: return value;
        }
      case 'contractType':
        switch (value.toUpperCase()) {
          case 'CLT': return 'CLT';
          case 'PJ': return 'PJ';
          case 'TEMPORÁRIO': return 'Temporário';
          case 'TEMPORARIO': return 'Temporário';
          case 'ESTAGIÁRIO': return 'Estagiário';
          case 'ESTAGIARIO': return 'Estagiário';
          case 'AUTÔNOMO': return 'Autônomo';
          case 'AUTONOMO': return 'Autônomo';
          default: return value;
        }
      case 'shift':
        switch (value.toLowerCase()) {
          case 'diurno': return 'Diurno';
          case 'noturno': return 'Noturno';
          case 'integral': return 'Integral';
          case 'manhã': return 'Manhã';
          case 'manha': return 'Manhã';
          case 'tarde': return 'Tarde';
          case 'noite': return 'Noite';
          default: return value;
        }
      case 'accountType':
        switch (value.toLowerCase()) {
          case 'corrente': return 'Conta Corrente';
          case 'poupança': return 'Conta Poupança';
          case 'poupanca': return 'Conta Poupança';
          case 'salário': return 'Conta Salário';
          case 'salario': return 'Conta Salário';
          case 'pagamento': return 'Conta Pagamento';
          default: return value;
        }
      case 'addressType':
        switch (value.toLowerCase()) {
          case 'residencial': return 'Residencial';
          case 'comercial': return 'Comercial';
          case 'correspondência': return 'Correspondência';
          case 'correspondencia': return 'Correspondência';
          default: return value;
        }
      case 'contactType':
        switch (value.toLowerCase()) {
          case 'telefone': return 'Telefone';
          case 'email': return 'Email';
          case 'whatsapp': return 'WhatsApp';
          default: return value;
        }
      case 'relationship':
        switch (value.toLowerCase()) {
          case 'próprio': return 'Próprio';
          case 'proprio': return 'Próprio';
          case 'familiar': return 'Familiar';
          case 'amigo': return 'Amigo';
          case 'trabalho': return 'Trabalho';
          default: return value;
        }
      case 'salaryType':
        switch (value.toLowerCase()) {
          case 'mensal': return 'Mensal';
          case 'por hora': return 'Por Hora';
          case 'por projeto': return 'Por Projeto';
          case 'comissão': return 'Comissão';
          case 'comissao': return 'Comissão';
          default: return value;
        }
      default:
        return value;
    }
  }

  // Mapear valores dos enums para exibição na edição
  mapEnumValuesForEditing(): void {
    if (!this.editData) return;
    
    
    // Mapear dados básicos do staff
    if (this.editData.staff) {
      if (this.editData.staff.gender) {
        const mappedGender = this.mapEnumValue('gender', this.editData.staff.gender);
      }
      
      if (this.editData.staff.maritalStatus) {
        const mappedMaritalStatus = this.mapEnumValue('maritalStatus', this.editData.staff.maritalStatus);
      }
      
      if (this.editData.staff.contractType) {
        const mappedContractType = this.mapEnumValue('contractType', this.editData.staff.contractType);
      }
      
      if (this.editData.staff.shift) {
        const mappedShift = this.mapEnumValue('shift', this.editData.staff.shift);
      }
      
      if (this.editData.staff.accountType) {
        const mappedAccountType = this.mapEnumValue('accountType', this.editData.staff.accountType);
      }
    }
    
    // Mapear endereços
    if (this.editData.addresses && this.editData.addresses.length > 0) {
      this.editData.addresses.forEach((address, index) => {
        if (address.addressType) {
          const mappedAddressType = this.mapEnumValue('addressType', address.addressType);
        }
      });
    }
    
    // Mapear contatos
    if (this.editData.contacts && this.editData.contacts.length > 0) {
      this.editData.contacts.forEach((contact, index) => {
        if (contact.contactType) {
          const mappedContactType = this.mapEnumValue('contactType', contact.contactType);
        }
        if (contact.relationship) {
          const mappedRelationship = this.mapEnumValue('relationship', contact.relationship);
        }
      });
    }
    
    // Mapear salário
    if (this.editData.salary && this.editData.salary.salaryType) {
      const mappedSalaryType = this.mapEnumValue('salaryType', this.editData.salary.salaryType);
    }
    
  }

  // Mapear automaticamente valores dos enums quando carregados do banco
  mapEnumValuesFromDatabase(): void {
    if (!this.staffComplete) return;
    
    
    // Mapear dados básicos
    if (this.staffComplete.Staff.Gender) {
      const mappedGender = this.mapEnumValue('gender', this.staffComplete.Staff.Gender);
    }
    
    if (this.staffComplete.Staff.MaritalStatus) {
      const mappedMaritalStatus = this.mapEnumValue('maritalStatus', this.staffComplete.Staff.MaritalStatus);
    }
    
    if (this.staffComplete.Staff.ContractType) {
      const mappedContractType = this.mapEnumValue('contractType', this.staffComplete.Staff.ContractType);
    }
    
    if (this.staffComplete.Staff.Shift) {
      const mappedShift = this.mapEnumValue('shift', this.staffComplete.Staff.Shift);
    }
    
    if (this.staffComplete.Staff.AccountType) {
      const mappedAccountType = this.mapEnumValue('accountType', this.staffComplete.Staff.AccountType);
    }
    
    // Mapear endereços
    if (this.staffComplete.Addresses && this.staffComplete.Addresses.length > 0) {
      this.staffComplete.Addresses.forEach((address, index) => {
        if (address.AddressType) {
          const mappedAddressType = this.mapEnumValue('addressType', address.AddressType);
        }
      });
    }
    
    // Mapear contatos
    if (this.staffComplete.Contacts && this.staffComplete.Contacts.length > 0) {
      this.staffComplete.Contacts.forEach((contact, index) => {
        if (contact.ContactType) {
          const mappedContactType = this.mapEnumValue('contactType', contact.ContactType);
        }
        if (contact.Relationship) {
          const mappedRelationship = this.mapEnumValue('relationship', contact.Relationship);
        }
      });
    }
    
    // Mapear salários
    if (this.staffComplete.SalaryHistory && this.staffComplete.SalaryHistory.length > 0) {
      this.staffComplete.SalaryHistory.forEach((salary, index) => {
        if (salary.SalaryType) {
          const mappedSalaryType = this.mapEnumValue('salaryType', salary.SalaryType);
        }
      });
    }
    
  }

  // Funções helper para exibição de endereços
  getAddressesForDisplay(): any[] {
    if (this.isEditing) {
      return this.editData?.addresses || [];
    } else {
      return this.staffComplete?.Addresses || [];
    }
  }

  getAddressDisplayValue(address: any, field: string): string {
    if (this.isEditing) {
      return address[field] || '-';
    } else {
      switch (field) {
        case 'type': return address.AddressType || '-';
        case 'street': return address.Street || '-';
        case 'number': return address.Number || '-';
        case 'complement': return address.Complement || '-';
        case 'neighborhood': return address.Neighborhood || '-';
        case 'city': return address.City || '-';
        case 'state': return address.State || '-';
        case 'zipCode': return address.ZipCode || '-';
        case 'country': return address.Country || '-';
        case 'isDefault': return address.IsDefault ? 'Sim' : 'Não';
        default: return '-';
      }
    }
  }

  getAddressEditValue(address: any, field: string): any {
    if (this.isEditing) {
      return address[field] || '';
    } else {
      switch (field) {
        case 'type': return address.AddressType || 'Residencial';
        case 'street': return address.Street || '';
        case 'number': return address.Number || '';
        case 'complement': return address.Complement || '';
        case 'neighborhood': return address.Neighborhood || '';
        case 'city': return address.City || '';
        case 'state': return address.State || '';
        case 'zipCode': return address.ZipCode || '';
        case 'country': return address.Country || 'Brasil';
        case 'isDefault': return address.IsDefault || false;
        default: return '';
      }
    }
  }

  // Funções helper para exibição de dados bancários
  getBankingDisplayValue(field: string): string {
    if (!this.staffComplete?.Staff) return '-';
    
    const staff = this.staffComplete.Staff;
    switch (field) {
      case 'bankName': return staff.BankName || '-';
      case 'agency': return staff.Agency || '-';
      case 'accountType': return staff.AccountType ? this.mapEnumValue('accountType', staff.AccountType) : '-';
      case 'accountNumber': return staff.AccountNumber || '-';
      case 'accountHolderName': return staff.AccountHolderName || '-';
      case 'pixKey': return staff.PIXKey || '-';
      default: return '-';
    }
  }

  // Funções helper para exibição de contatos
  getContactsForDisplay(): any[] {
    if (this.isEditing) {
      return this.editData?.contacts || [];
    } else {
      return this.staffComplete?.Contacts || [];
    }
  }

  getContactDisplayValue(contact: any, field: string): string {
    if (this.isEditing) {
      return contact[field] || '-';
    } else {
      switch (field) {
        case 'contactType': return contact.ContactType || '-';
        case 'contactName': return contact.ContactName || '-';
        case 'phoneNumber': return contact.PhoneNumber || '-';
        case 'relationship': return contact.Relationship || '-';
        case 'isEmergencyContact': return contact.IsEmergencyContact ? 'Sim' : 'Não';
        case 'isDefault': return contact.IsDefault ? 'Sim' : 'Não';
        default: return '-';
      }
    }
  }

  // Funções helper para exibição de dados do contrato
  getContractDisplayValue(field: string): string {
    if (!this.staffComplete?.Staff) return '-';
    
    const staff = this.staffComplete.Staff;
    switch (field) {
      case 'contractType': return staff.ContractType ? this.mapEnumValue('contractType', staff.ContractType) : '-';
      case 'shift': return staff.Shift ? this.mapEnumValue('shift', staff.Shift) : '-';
      default: return '-';
    }
  }

  // Função helper para exibição do horário de trabalho
  getWorkScheduleDisplay(): string {
    if (!this.staffComplete?.Staff) return '-';
    
    const staff = this.staffComplete.Staff;
    if (staff.WorkSchedule) {
      return staff.WorkSchedule;
    }
    return '-';
  }

  // Função helper para exibição de dados salariais
  getSalaryDisplayValue(field: string): string {
    const salary = this.getCurrentSalary();
    if (!salary) return '-';
    
    switch (field) {
      case 'baseSalary': return salary.BaseSalary ? `R$ ${salary.BaseSalary.toFixed(2)}` : '-';
      case 'grossSalary': return salary.GrossSalary ? `R$ ${salary.GrossSalary.toFixed(2)}` : '-';
      case 'netSalary': return salary.NetSalary ? `R$ ${salary.NetSalary.toFixed(2)}` : '-';
      case 'currency': return salary.Currency || 'BRL';
      case 'salaryType': return salary.SalaryType ? this.mapEnumValue('salaryType', salary.SalaryType) : '-';
      case 'effectiveDate': return salary.EffectiveDate ? this.formatDate(salary.EffectiveDate) : '-';
      case 'endDate': return salary.EndDate ? this.formatDate(salary.EndDate) : '-';
      case 'notes': return salary.Notes || '-';
      case 'reason': return salary.Reason || '-';
      default: return '-';
    }
  }

  /**
   * Retorna o histórico salarial ordenado do mais recente para o mais antigo.
   * Garante que o salário atual/mais novo apareça primeiro na tabela.
   */
  getSortedSalaryHistory(): any[] {
    const history = this.staffComplete?.SalaryHistory;
    if (!history || !Array.isArray(history)) {
      return [];
    }

    return [...history].sort((a: any, b: any) => {
      const aDate = a.EffectiveDate ? new Date(a.EffectiveDate).getTime() : 0;
      const bDate = b.EffectiveDate ? new Date(b.EffectiveDate).getTime() : 0;
      return bDate - aDate;
    });
  }

  // Consultar CEP via ViaCEP API
  async consultarCEP(cep: string, addressIndex: number = 0): Promise<void> {
    if (!cep || cep.length < 8) return;
    
    // Limpar CEP (remover caracteres especiais)
    const cepLimpo = cep.replace(/\D/g, '');
    
    if (cepLimpo.length !== 8) return;
    
    try {
      
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        return;
      }
      
      
      // Preencher automaticamente os campos de endereço
      if (this.editData?.addresses && this.editData.addresses[addressIndex]) {
        this.editData.addresses[addressIndex].street = data.logradouro || '';
        this.editData.addresses[addressIndex].neighborhood = data.bairro || '';
        this.editData.addresses[addressIndex].city = data.localidade || '';
        this.editData.addresses[addressIndex].state = data.uf || '';
        this.editData.addresses[addressIndex].country = 'Brasil';
        
        // Forçar atualização da UI
        this.cdr.detectChanges();
        
      }
      
    } catch (error) {
      console.error('❌ Erro ao consultar CEP:', error);
    }
  }

  // Extrair nome do arquivo do caminho
  extractFileNameFromPath(filePath: string): string {
    if (!filePath) return '';
    return filePath.split('/').pop() || filePath.split('\\').pop() || '';
  }

  // Verificar se é arquivo de imagem
  isImageFile(fileExtension: string): boolean {
    if (!fileExtension) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => fileExtension.toLowerCase().includes(ext.toLowerCase()));
  }

  // Download de documento
  downloadDocument(attachment: any): void {
    if (!attachment) return;
    
    const filePath = attachment.FilePath || attachment.filePath;
    
    if (!filePath) {
      console.error('❌ FilePath não encontrado no attachment');
      return;
    }

    // Se o FilePath já é uma URL completa do blob, usa diretamente
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      window.open(filePath, '_blank');
      return;
    }

    // Fallback: construir URL via API (para casos legados)
    const staffId = this.staffComplete?.Staff?.Id;
    const fileName = this.extractFileNameFromPath(filePath);
    
    if (!staffId || !fileName) {
      console.error('❌ Dados insuficientes para download:', { staffId, fileName });
      return;
    }

    const downloadUrl = `${environment.microservices.staff}/staff/blob/download-document?staffId=${staffId}&fileName=${encodeURIComponent(fileName)}`;
    window.open(downloadUrl, '_blank');
  }

  // ===== MÉTODOS AUXILIARES PARA DOCUMENTOS EXISTENTES =====
  
  // Obtém o tipo de documento de um attachment (com fallback para inferir pelo nome)
  getAttachmentDocumentType(att: any): string {
    // Primeiro tenta obter o documentType direto
    let docType = att.documentType || att.DocumentType || '';
    
    // Normalizar o documentType para comparação (remove underscore, hífen e converte para minúsculo)
    if (docType) {
      // Mapear tipos conhecidos do banco para os tipos do frontend
      const typeNormalization: { [key: string]: string } = {
        'residenceproof': 'residenceProof',
        'residence_proof': 'residenceProof',
        'medicalcertificate': 'medicalCertificate',
        'medical_certificate': 'medicalCertificate',
        'workcard': 'workCard',
        'work_card': 'workCard',
        'photo': 'photo',
        'rg': 'rg',
        'cpf': 'cpf',
        'cnh': 'cnh',
        'diploma': 'diploma',
        'curriculum': 'curriculum',
        'other': 'other'
      };
      
      const normalizedType = docType.toLowerCase().replace(/[-_]/g, '');
      if (typeNormalization[normalizedType]) {
        return typeNormalization[normalizedType];
      }
      
      // Se não encontrou no mapeamento, retorna o tipo normalizado
      return docType;
    }
    
    // Se não tiver documentType, tenta inferir pelo nome do documento
    if (att.documentName || att.DocumentName) {
      const docName = (att.documentName || att.DocumentName || '').toLowerCase();
      
      // Mapeamento de palavras-chave para tipos de documento
      // Inclui os labels dos DOCUMENT_TYPES e variações comuns
      const typeMapping: { [key: string]: string[] } = {
        'photo': ['foto', 'photo', 'imagem', 'avatar', 'foto do perfil', 'perfil'],
        'rg': ['rg', 'identidade', 'carteira de identidade'],
        'cpf': ['cpf'],
        'cnh': ['cnh', 'habilitação', 'habilitacao', 'carteira de motorista'],
        'workCard': ['carteira de trabalho', 'ctps', 'work card', 'workcard'],
        'diploma': ['diploma', 'certificado', 'escolar', 'escolaridade'],
        'curriculum': ['currículo', 'curriculo', 'cv', 'curriculum'],
        'residenceProof': ['comprovante de residência', 'comprovante de residencia', 'comprovante residência', 'comprovante residencia', 'residência', 'residencia', 'endereco', 'endereço', 'residenceproof'],
        'medicalCertificate': ['atestado médico', 'atestado medico', 'atestado', 'aso', 'saúde', 'saude', 'health', 'medicalcertificate'],
        'other': ['outro', 'outros', 'other']
      };
      
      for (const [type, keywords] of Object.entries(typeMapping)) {
        if (keywords.some(keyword => docName.includes(keyword))) {
          return type;
        }
      }
    }
    
    return docType;
  }
  
  // Verifica se já existe um documento cadastrado para o tipo especificado
  hasExistingDocument(docType: string): boolean {
    const attachments = this.editData?.attachments || [];
    return attachments.some((att: any) => {
      const documentType = this.getAttachmentDocumentType(att);
      return documentType.toLowerCase() === docType.toLowerCase();
    });
  }

  // Obtém o nome do documento existente para o tipo especificado
  getExistingDocumentName(docType: string): string {
    const attachments = this.editData?.attachments || [];
    const attachment = attachments.find((att: any) => {
      const documentType = this.getAttachmentDocumentType(att);
      return documentType.toLowerCase() === docType.toLowerCase();
    }) as any;
    return attachment?.documentName || attachment?.DocumentName || '-';
  }

  // Obtém o tamanho do documento existente para o tipo especificado
  getExistingDocumentSize(docType: string): number {
    const attachments = this.editData?.attachments || [];
    const attachment = attachments.find((att: any) => {
      const documentType = this.getAttachmentDocumentType(att);
      return documentType.toLowerCase() === docType.toLowerCase();
    }) as any;
    return attachment?.fileSize || attachment?.FileSize || 0;
  }

  // Obtém o anexo existente por tipo
  getExistingAttachment(docType: string): any {
    const attachments = this.editData?.attachments || [];
    return attachments.find((att: any) => {
      const documentType = this.getAttachmentDocumentType(att);
      return documentType.toLowerCase() === docType.toLowerCase();
    });
  }

  // Download de documento existente por tipo
  downloadExistingDocument(docType: string): void {
    const attachment = this.getExistingAttachment(docType);
    if (attachment) {
      this.downloadDocument(attachment);
    }
  }

  // Remove documento existente por tipo
  removeExistingDocument(docType: string): void {
    const attachments = this.editData?.attachments || [];
    const index = attachments.findIndex((att: any) => {
      const documentType = this.getAttachmentDocumentType(att);
      return documentType.toLowerCase() === docType.toLowerCase();
    });
    if (index !== -1) {
      this.removeAttachment(index);
    }
  }

  // ===== MÉTODOS PARA DOCUMENTOS "OUTROS" (MÚLTIPLOS) =====
  
  // Selecionar arquivo para "outros"
  onOtherFileSelected(event: any): void {
    const file = event.target?.files?.[0];
    if (!file) return;
    
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const newDoc = {
      file: file,
      documentName: file.name,
      fileSize: file.size,
      fileExtension: fileExtension,
      mimeType: file.type,
      status: 'selecionado',
      uploaded: false,
      url: null
    };
    
    this.otherDocuments.push(newDoc);
    
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    event.target.value = '';
  }
  
  // Upload de documento "outro" para o backend
  uploadOtherDocument(index: number): void {
    const doc = this.otherDocuments[index];
    if (!doc || !doc.file) return;
    
    const staffId = this.staffComplete?.Staff?.Id;
    if (!staffId) {
      console.error('❌ StaffId não disponível para upload');
      return;
    }
    
    doc.status = 'enviando';
    
    this.staffService.uploadDocument(doc.file, staffId, 'other', doc.documentName).subscribe({
      next: (response: any) => {
        
        // Extrair a URL do blob (verificar tanto camelCase quanto PascalCase)
        const blobUrl = response.url || response.Url || response.filePath || response.FilePath || '';
        
        doc.status = 'enviado';
        doc.uploaded = true;
        doc.url = blobUrl;
        
        // Adicionar aos attachments do editData
        if (!this.editData!.attachments) {
          this.editData!.attachments = [];
        }
        
        const newAttachment = {
          documentName: response.documentName || response.DocumentName || doc.documentName,
          documentType: 'other',
          status: 'Aprovado',
          filePath: blobUrl,
          fileExtension: response.fileExtension || response.FileExtension || doc.fileExtension,
          mimeType: doc.mimeType,
          fileSize: response.fileSize || response.FileSize || doc.fileSize
        };
        
        this.editData!.attachments.push(newAttachment);
      },
      error: (error) => {
        console.error('❌ Erro no upload "outro":', error);
        doc.status = 'erro';
      }
    });
  }
  
  // Remover documento "outro" pendente (não enviado)
  removeOtherDocument(index: number): void {
    const doc = this.otherDocuments[index];
    if (doc?.uploaded && doc?.url) {
      // Se já foi enviado, remover do editData também
      const attachments = this.editData?.attachments || [];
      const attIndex = attachments.findIndex((att: any) => att.filePath === doc.url);
      if (attIndex !== -1) {
        attachments.splice(attIndex, 1);
      }
    }
    this.otherDocuments.splice(index, 1);
  }
  
  // Obter todos os documentos "outros" existentes (já salvos no banco)
  getExistingOtherDocuments(): any[] {
    const attachments = this.editData?.attachments || [];
    return attachments.filter((att: any) => {
      const documentType = this.getAttachmentDocumentType(att);
      return documentType.toLowerCase() === 'other';
    });
  }
  
  // Remover documento "outro" existente (do banco)
  removeExistingOtherDocument(attachment: any): void {
    const attachments = this.editData?.attachments || [];
    const index = attachments.findIndex((att: any) => att === attachment);
    if (index !== -1) {
      this.removeAttachment(index);
    }
  }
  
  // Formatar tamanho do arquivo
  formatFileSizeOther(bytes: number): string {
    if (!bytes || bytes === 0) return '-';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // ===== MÁSCARAS VISUAIS =====
  
  // Aplicar máscara de CEP (00000-000)
  applyCepMask(value: string): string {
    if (!value) return '';
    const cepLimpo = value.replace(/\D/g, '');
    if (cepLimpo.length <= 5) return cepLimpo;
    return cepLimpo.substring(0, 5) + '-' + cepLimpo.substring(5, 8);
  }

  // Remover máscara de CEP (retorna apenas números)
  removeCepMask(value: string): string {
    return value ? value.replace(/\D/g, '') : '';
  }

  // Aplicar máscara de agência bancária (0000)
  applyAgencyMask(value: string): string {
    if (!value) return '';
    return value
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, '')
      .substring(0, 10);
  }

  // Remover máscara de agência
  removeAgencyMask(value: string): string {
    return value ? value.toUpperCase().replace(/[^A-Z0-9-]/g, '') : '';
  }

  // Aplicar máscara de conta bancária (00000-0)
  applyAccountMask(value: string): string {
    if (!value) return '';
    return value
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, '')
      .substring(0, 20);
  }

  // Remover máscara de conta
  removeAccountMask(value: string): string {
    return value ? value.toUpperCase().replace(/[^A-Z0-9-]/g, '') : '';
  }

  // Aplicar máscara de salário (R$ 0.000,00)
  applySalaryMask(value: string): string {
    if (!value) return '';
    const salary = parseFloat(value) || 0;
    return salary.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  // Remover máscara de salário (retorna apenas números)
  removeSalaryMask(value: string): string {
    if (!value) return '';
    return value.replace(/[^\d,.-]/g, '').replace(',', '.');
  }

  // Aplicar máscara de CPF (000.000.000-00)
  applyCpfMask(value: string): string {
    if (!value) return '';
    const cpfLimpo = value.replace(/\D/g, '');
    if (cpfLimpo.length <= 3) return cpfLimpo;
    if (cpfLimpo.length <= 6) return cpfLimpo.substring(0, 3) + '.' + cpfLimpo.substring(3);
    if (cpfLimpo.length <= 9) return cpfLimpo.substring(0, 3) + '.' + cpfLimpo.substring(3, 6) + '.' + cpfLimpo.substring(6);
    return cpfLimpo.substring(0, 3) + '.' + cpfLimpo.substring(3, 6) + '.' + cpfLimpo.substring(6, 9) + '-' + cpfLimpo.substring(9, 11);
  }

  // Remover máscara de CPF
  removeCpfMask(value: string): string {
    return value ? value.replace(/\D/g, '') : '';
  }

  // Aplicar máscara de RG (00.000.000-0)
  applyRgMask(value: string): string {
    if (!value) return '';
    const rgLimpo = value.replace(/\D/g, '');
    if (rgLimpo.length <= 2) return rgLimpo;
    if (rgLimpo.length <= 5) return rgLimpo.substring(0, 2) + '.' + rgLimpo.substring(2);
    if (rgLimpo.length <= 8) return rgLimpo.substring(0, 2) + '.' + rgLimpo.substring(2, 5) + '.' + rgLimpo.substring(5);
    return rgLimpo.substring(0, 2) + '.' + rgLimpo.substring(2, 5) + '.' + rgLimpo.substring(5, 8) + '-' + rgLimpo.substring(8, 9);
  }

  // Remover máscara de RG
  removeRgMask(value: string): string {
    return value ? value.replace(/\D/g, '') : '';
  }

  // Aplicar máscara de telefone ((00) 00000-0000)
  applyPhoneMask(value: string): string {
    if (!value) return '';
    const phoneLimpo = value.replace(/\D/g, '');
    if (phoneLimpo.length <= 2) return phoneLimpo;
    if (phoneLimpo.length <= 7) return '(' + phoneLimpo.substring(0, 2) + ') ' + phoneLimpo.substring(2);
    return '(' + phoneLimpo.substring(0, 2) + ') ' + phoneLimpo.substring(2, 7) + '-' + phoneLimpo.substring(7, 11);
  }

  // Remover máscara de telefone
  removePhoneMask(value: string): string {
    return value ? value.replace(/\D/g, '') : '';
  }

  // Métodos para gerenciar benefícios
  getBenefitsForDisplay(): any[] {
    // Sempre usar editData.benefits se disponível (já foi mapeado no prepareViewData)
    if (this.editData?.benefits && Array.isArray(this.editData.benefits) && this.editData.benefits.length > 0) {
      return this.editData.benefits;
    }
    // Fallback para staffComplete se editData não tiver benefícios
    if (this.staffComplete?.Benefits && Array.isArray(this.staffComplete.Benefits) && this.staffComplete.Benefits.length > 0) {
      return this.staffComplete.Benefits.map(b => ({
        benefitType: b.BenefitType || '',
        amount: b.Amount ?? 0,
        currency: b.Currency || 'BRL',
        startDate: b.StartDate || '',
        isActive: b.IsActive ?? true
      }));
    }
    return [];
  }

  // Getters para garantir que os valores sejam sempre atualizados
  getSalaryBase(): number {
    const value = this.editData?.salary?.baseSalary ?? 0;
    return value;
  }

  getSalaryGross(): number {
    const value = this.editData?.salary?.grossSalary ?? 0;
    return value;
  }

  getSalaryNet(): number {
    const value = this.editData?.salary?.netSalary ?? 0;
    return value;
  }

  getSalaryType(): string {
    const value = this.editData?.salary?.salaryType || 'Mensal';
    return value;
  }

  getSalaryNotes(): string {
    const value = this.editData?.salary?.notes || '';
    return value;
  }

  addBenefit(): void {
    if (!this.editData) {
      this.editData = {
        staff: {} as any,
        contract: {} as any,
        contacts: [],
        addresses: [],
        benefits: [],
        attachments: []
      };
    }
    if (!this.editData.benefits) {
      this.editData.benefits = [];
    }
    this.editData.benefits.push({
      benefitType: '',
      amount: 0,
      currency: 'BRL',
      startDate: new Date().toISOString(),
      isActive: true
    });
    this.cdr.detectChanges();
  }

  removeBenefit(index: number): void {
    if (this.editData?.benefits && this.editData.benefits.length > 1) {
      this.editData.benefits.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  // TrackBy para melhorar performance do ngFor
  trackByBenefitIndex(index: number, benefit: any): any {
    return index;
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
